import { useState, useEffect } from "react";
import { triggerGlobalToast } from "~/utils/globalToast";

/**
 * Hook to monitor online/offline status using the Navigator API
 * Automatically triggers an error toast when the user goes offline
 *
 * @returns {boolean} isOnline - Current online status
 *
 * @example
 * const isOnline = useOnlineStatus();
 * if (!isOnline) {
 *   // Handle offline state
 * }
 */
export const useOnlineStatus = (): boolean => {
  // Initialize with current online status
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // Guard for non-browser contexts (SSR, Node tests)
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return true; // Default to online in non-browser contexts
    }
    return navigator.onLine;
  });

  useEffect(() => {
    // Guard for non-browser contexts
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      // Trigger error toast when going offline
      triggerGlobalToast({
        severity: "error",
        summary: "No Internet Connection",
        detail:
          "You are currently offline. Please check your internet connection.",
        life: 5000,
      });
    };

    // Set initial state
    const initialOnlineStatus = navigator.onLine;
    setIsOnline(initialOnlineStatus);

    // Trigger error toast on initial load if already offline
    if (!initialOnlineStatus) {
      triggerGlobalToast({
        severity: "error",
        summary: "No Internet Connection",
        detail:
          "You are currently offline. Please check your internet connection.",
        life: 5000,
      });
    }

    // Listen to online/offline events
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
};

export default useOnlineStatus;
